import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useScene } from '../scene-context';

type Petal = { id: number; x: number; y: number; rotate: number };

export function PetalShower({ children }: { children: ReactNode }) {
  const { pack } = useScene();
  const reduceMotion = useReducedMotion();
  const [petals, setPetals] = useState<Petal[]>([]);

  const addBurst = (x: number, y: number) => {
    const burst = Array.from({ length: 18 }, (_, i) => ({
      id: Date.now() + i,
      x: x + (Math.random() - 0.5) * 120,
      y,
      rotate: Math.random() * 260,
    }));
    setPetals((old) => [...old.slice(-24), ...burst]);
  };

  const shower = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    addBurst(event.clientX - rect.left, event.clientY - rect.top);
  };

  const showerFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (reduceMotion || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    addBurst(rect.width / 2, rect.height / 2);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Start the petal shower"
      onKeyDown={showerFromKeyboard}
      onPointerDown={shower}
      className="relative z-10 w-full max-w-3xl px-6 text-center"
    >
      {children}
      <p className="mt-5 text-xs font-semibold uppercase" style={{ color: pack.colors.primary, letterSpacing: 0 }}>
        A shower of blessings
      </p>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {petals.map((petal) => (
          <motion.span
            key={petal.id}
            className="absolute h-4 w-3 rounded-full"
            style={{
              left: petal.x,
              top: petal.y,
              background: `linear-gradient(135deg, ${pack.colors.primary}, ${pack.colors.accent})`,
            }}
            initial={{ opacity: 0, y: 0, rotate: petal.rotate, scale: 0.7 }}
            animate={{ opacity: [0, 1, 0], y: [0, -90, 220], x: [0, Math.random() * 90 - 45], rotate: petal.rotate + 180, scale: [0.7, 1, 0.55] }}
            transition={{ duration: 2.4, ease: 'easeOut' }}
          />
        ))}
      </div>
    </div>
  );
}
